import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Path, Warning, ArrowRight, CheckCircle, XCircle, Coins, Medal, Sparkle, Compass, Lightning, Crown, Users,
} from "@phosphor-icons/react";
import api, { apiErr } from "../api";
import { useConnectivity } from "../context/ConnectivityContext";
import { useAudio } from "../context/AudioContext";
import { GameplayEvent } from "../audio/gameplayAudioEvents";
import { useBeatVisual } from "../hooks/useBeatVisual";
import SkillChallenge from "../components/SkillChallenge";
import RingChallenge from "../components/RingChallenge";
import GoldCounter from "../components/GoldCounter";
import ThreatDebrief from "../components/ThreatDebrief";
import GuidanceBanner from "../components/GuidanceBanner";
import { useBeta } from "../context/BetaContext";

const DANGER_UI = {
  calm: { label: "Calm", color: "#4ade80", segs: 1 },
  alert: { label: "Alert", color: "#fbbf24", segs: 2 },
  danger: { label: "Dangerous", color: "#fb923c", segs: 3 },
  critical: { label: "Critical", color: "#f87171", segs: 4 },
};
const REASON_TEXT = {
  hostile_route: "Hostile route", crew_injury_risk: "Crew injury risk",
  crew_stress: "Crew under stress", resource_pressure: "Resources stretched",
  poor_decisions: "Risky choices", steady_progress: "Steady progress",
  active_challenge: "Challenge underway", threat_reduced: "Threat reduced",
  challenge_setback: "Setback", environmental_hazard: "Environmental hazard",
  risky_choice: "Bold gamble",
};

// Restrained, sound-free threat indicator driven by the server-authoritative danger level.
// A subtle bar-aligned glow pulse plays only when beat visuals are active (founder preview,
// beat-sync on, no reduced motion); otherwise it renders as a plain, immediate state change.
function DangerIndicator({ danger }) {
  const level = danger && danger.level ? danger.level : null;
  const tense = level === "danger" || level === "critical";
  const { pulse } = useBeatVisual({ event: "bar_start", effect: "danger_transition", intensity: tense ? "moderate" : "subtle", enabled: !!level });
  if (!level) return null;
  const cfg = DANGER_UI[level] || DANGER_UI.calm;
  return (
    <div className="mb-4 rounded-sm border border-ff-brass/25 bg-ff-bg/40 px-3 py-2" data-testid="danger-indicator">
      <div className="flex items-center justify-between">
        <span className="font-mono-ff text-[10px] uppercase tracking-widest text-ff-muted">Threat level</span>
        <span className="text-sm font-semibold" style={{ color: cfg.color }} data-testid="danger-level-label">{cfg.label}</span>
      </div>
      <div key={tense ? pulse : "static"} className={`mt-1.5 flex gap-1 rounded-full ${tense ? "bv-danger-pulse" : ""}`}
        style={{ "--bv-glow": `${cfg.color}59` }} data-testid="danger-meter">
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className="h-1.5 flex-1 rounded-full transition-colors duration-500"
            style={{ backgroundColor: i <= cfg.segs ? cfg.color : "rgba(255,255,255,0.08)" }} />
        ))}
      </div>
      {(danger.reasons || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {danger.reasons.map((r) => (
            <span key={r} className="rounded-full border border-ff-brass/20 px-2 py-0.5 text-[10px] text-ff-muted">{REASON_TEXT[r] || r}</span>
          ))}
        </div>
      )}
    </div>
  );
}


export default function Mission() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const { setExpeditionActive } = useConnectivity();
  const audio = useAudio();
  const { markFirstExpeditionDone } = useBeta();
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | decisions | challenge | reveal | result
  const [current, setCurrent] = useState(0);
  const [challenge, setChallenge] = useState(null);
  const [challengeResult, setChallengeResult] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [danger, setDanger] = useState(null);
  const [debrief, setDebrief] = useState(null);

  const dispatchDanger = (d, trigger) => {
    if (!d) return;
    setDanger(d);
    audio?.gameplay({
      type: GameplayEvent.DANGER_LEVEL_CHANGED, level: d.level, score: d.score,
      version: d.version, reasons: d.reasons, fallback: d.fallback,
      trigger_event: trigger, confirmed: true,
    });
  };

  // Register an active expedition so the update prompt defers and offline copy adapts.
  useEffect(() => {
    setExpeditionActive(true);
    return () => setExpeditionActive(false);
  }, [setExpeditionActive]);

  // Once the result is reached the expedition is finalized (no longer "in progress").
  useEffect(() => {
    if (phase === "result") setExpeditionActive(false);
  }, [phase, setExpeditionActive]);

  useEffect(() => {
    api.get(`/sessions/${sessionId}`)
      .then((r) => {
        const s = r.data;
        setSession(s);
        if (s.status === "completed") {
          nav(`/history/${sessionId}`, { replace: true });
          return;
        }
        const exp = s.expedition_id || s.expedition_name;
        audio?.gameplay({ type: GameplayEvent.SESSION_STARTED, expedition: exp, confirmed: true });
        // restrained crew-selection motif for the highlighted (first) confirmed crew member
        if (Array.isArray(s.crew_ids) && s.crew_ids[0] && s.decisions.length === 0) {
          audio?.gameplay({ type: GameplayEvent.CREW_SELECTED, crew_id: s.crew_ids[0], session_id: sessionId, confirmed: true, event_version: "crew-audio-v1" });
        }
        dispatchDanger(s.danger, "session_resumed");
        const answered = s.decisions.length;
        const total = s.decisions_content.length;
        if (answered < total) {
          setCurrent(answered);
          setPhase("decisions");
          audio?.gameplay({ type: GameplayEvent.DECISION_PRESENTED, expedition: exp });
        } else if (!s.challenge || !s.challenge.submitted) {
          startChallenge();
        } else {
          setPhase("reveal");
        }
      })
      .catch((e) => {
        toast.error(apiErr(e));
        nav("/dashboard");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const startChallenge = useCallback(async () => {
    try {
      const { data } = await api.post(`/sessions/${sessionId}/challenge/start`);
      setChallenge(data);
      setPhase("challenge");
      audio?.gameplay({ type: GameplayEvent.SKILL_CHALLENGE_STARTED, confirmed: true, tempo: data?.tempo });
      dispatchDanger(data.danger, "skill_challenge_started");
    } catch (e) {
      toast.error(apiErr(e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const chooseOption = async (optionId) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${sessionId}/decisions`, { index: current, option_id: optionId });
      audio?.gameplay({ type: GameplayEvent.DECISION_CONFIRMED, expedition: session?.expedition_id || session?.expedition_name, confirmed: true });
      dispatchDanger(data.danger, "decision_confirmed");
      if (data.all_answered) {
        await startChallenge();
      } else {
        setCurrent(data.answered);
        audio?.gameplay({ type: GameplayEvent.DECISION_PRESENTED, expedition: session?.expedition_id || session?.expedition_name });
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const submitChallenge = async (value) => {
    setBusy(true);
    try {
      const body = { challenge_id: challenge.challenge_id };
      if (Array.isArray(value)) body.ring_positions = value;
      else body.stopped_position = value;
      const { data } = await api.post(`/sessions/${sessionId}/challenge/submit`, body);
      setChallengeResult(data);
      setPhase("reveal");
      audio?.gameplay({ type: GameplayEvent.SKILL_CHALLENGE_COMPLETED, expedition: session?.expedition_id || session?.expedition_name, confirmed: true });
      dispatchDanger(data.danger, "skill_challenge_completed");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const revealOutcome = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${sessionId}/complete`);
      setResult(data.result);
      markFirstExpeditionDone && markFirstExpeditionDone();
      api.get(`/sessions/${sessionId}/debrief`).then((r) => setDebrief(r.data)).catch(() => {});
      setPhase("result");
      const res = data.result;
      audio?.gameplay({ type: GameplayEvent.OUTCOME_CONFIRMED, session_id: sessionId, outcome: res.outcome_label, version: res.final_score, confirmed: true });
      if (res.rank_up) audio?.gameplay({ type: GameplayEvent.RANK_PROMOTED, session_id: sessionId, rank: res.new_rank, confirmed: true });
      // authoritative crew motifs — dispatched verbatim from the server (supporting the outcome cue)
      (res.audio_crew_events || []).forEach((e) => audio?.gameplay({ ...e, type: e.event, confirmed: true }));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (phase === "loading" || !session) {
    return <div className="py-20 text-center text-ff-muted">Preparing the expedition...</div>;
  }

  const decisions = session.decisions_content;

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="font-mono-ff text-[10px] uppercase tracking-widest text-ff-brass">Expedition in progress</p>
          <h1 className="font-display text-2xl font-bold tracking-tight">{session.expedition_name}</h1>
        </div>
        <div className="rounded-sm border border-ff-brass/30 px-3 py-1 text-right">
          <div className="text-[10px] text-ff-muted">Stake at risk</div>
          <div className="font-mono-ff font-bold text-ff-gold">{session.stake}</div>
        </div>
      </div>

      {phase !== "result" && <DangerIndicator danger={danger} />}

      {(phase === "decisions") && (
        <>
          <GuidanceBanner id="mission-decisions" title="Making decisions">
            You'll face five decisions. Each option lists its advantage and its risk — weigh them, then tap the one you want. Your crew and the timing challenge also shape the result.
          </GuidanceBanner>
          <ProgressDots total={decisions.length} current={current} />
          <AnimatePresence mode="wait">
            <DecisionCard key={current} decision={decisions[current]} index={current} total={decisions.length} onChoose={chooseOption} busy={busy} />
          </AnimatePresence>
        </>
      )}

      {phase === "challenge" && challenge && (
        <>
          <GuidanceBanner id="mission-challenge" title="Timing challenge">
            Stop at the right moment — accuracy is validated on the server and adds to your final score. Threat above is descriptive only and never changes your result.
          </GuidanceBanner>
          {challenge.type === "rings"
            ? <RingChallenge challenge={challenge} onSubmit={submitChallenge} submitting={busy} />
            : <SkillChallenge challenge={challenge} onSubmit={submitChallenge} submitting={busy} />}
        </>
      )}

      {phase === "reveal" && (
        <RevealGate challengeResult={challengeResult} onReveal={revealOutcome} busy={busy} />
      )}

      {phase === "result" && result && (
        <div className="space-y-4">
          <ResultScreen result={result} onDashboard={() => nav("/dashboard")} onReplay={() => nav("/play")} />
          <ThreatDebrief debrief={debrief} />
        </div>
      )}
    </div>
  );
}

function ProgressDots({ total, current }) {
  return (
    <div className="mb-4 flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < current ? "bg-ff-gold" : i === current ? "bg-ff-brass" : "bg-ff-brass/20"}`} />
      ))}
    </div>
  );
}

function DecisionCard({ decision, index, total, onChoose, busy }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, rotateX: -6 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.45 }}
      className="ff-parchment-card rounded-md p-6 sm:p-8"
      data-testid={`decision-${index}`}
    >
      <div className="flex items-center gap-2 text-[#6b5323]">
        <Path size={20} weight="duotone" />
        <span className="font-mono-ff text-xs uppercase tracking-widest">Decision {index + 1} of {total}</span>
      </div>
      <p className="mt-3 font-display text-2xl font-semibold leading-snug text-[#1F1A12]">{decision.situation}</p>

      <div className="mt-6 space-y-3">
        {decision.options.map((o) => (
          <button
            key={o.id}
            onClick={() => onChoose(o.id)}
            disabled={busy}
            data-testid={`decision-option-${o.id}`}
            className="group w-full rounded-sm border border-[#8a6d1f]/40 bg-[#e9dcbe] p-4 text-left transition-all duration-200 hover:border-[#8a6d1f] hover:bg-[#e2d4b3] active:scale-[0.99] disabled:opacity-60"
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-[#1F1A12]">{o.label}</span>
              <ArrowRight size={16} className="text-[#8a6d1f] opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
            <div className="mt-2 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
              <span className="flex items-center gap-1 text-[#2D5A27]"><CheckCircle size={13} weight="fill" /> {o.advantage}</span>
              <span className="flex items-center gap-1 text-[#7a2020]"><Warning size={13} /> {o.risk}</span>
            </div>
          </button>
        ))}
      </div>
    </motion.div>
  );
}

function RevealGate({ challengeResult, onReveal, busy }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="ff-panel rounded-md p-8 text-center">
      <Compass size={44} weight="duotone" className="mx-auto animate-compass text-ff-gold" />
      {challengeResult && (
        <div className="mt-4" data-testid="challenge-result">
          <p className="text-sm text-ff-muted">Skill Challenge</p>
          <p className="font-display text-3xl font-bold text-ff-gold">{challengeResult.band}</p>
          <p className="font-mono-ff text-sm text-ff-muted">Score: {challengeResult.score}/100</p>
        </div>
      )}
      <p className="mx-auto mt-5 max-w-md text-sm italic text-ff-muted">
        Your choices, crew, and skill affect the outcome. A limited chance factor is also included.
      </p>
      <button
        onClick={onReveal}
        disabled={busy}
        data-testid="reveal-outcome-btn"
        className="mt-6 rounded-sm bg-ff-gold px-10 py-3.5 font-semibold text-ff-bg transition-all hover:bg-ff-brass hover:ff-glow-gold active:scale-95 disabled:opacity-60"
      >
        {busy ? "Resolving..." : "Reveal Outcome"}
      </button>
    </motion.div>
  );
}

const OUTCOME_STYLE = {
  "Critical Success": { color: "text-ff-gold", icon: Sparkle, ring: "border-ff-gold" },
  Success: { color: "text-ff-success", icon: CheckCircle, ring: "border-ff-success" },
  "Narrow Escape": { color: "text-ff-brass", icon: Lightning, ring: "border-ff-brass" },
  Failure: { color: "text-ff-danger", icon: XCircle, ring: "border-ff-danger" },
  "Major Loss": { color: "text-ff-danger", icon: XCircle, ring: "border-ff-danger" },
};

function ResultScreen({ result, onDashboard, onReplay }) {
  const style = OUTCOME_STYLE[result.outcome_label] || OUTCOME_STYLE.Success;
  const Icon = style.icon;
  const positive = result.gold_delta >= 0;
  const b = result.breakdown;

  const bars = [
    { label: "Base / Difficulty", value: b.difficulty_component, max: 15 },
    { label: "Crew Contribution", value: b.crew_component, max: 25 },
    { label: "Your Decisions", value: b.decision_component, max: 40 },
    { label: "Skill Challenge", value: b.skill_component, max: 15 },
  ];

  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} data-testid="result-screen">
      <div className={`ff-panel rounded-md border-2 ${style.ring} p-8 text-center`}>
        <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", delay: 0.15 }}>
          <Icon size={64} weight="duotone" className={`mx-auto ${style.color}`} />
        </motion.div>
        <h1 className={`mt-3 font-display text-4xl font-bold tracking-tight ${style.color}`} data-testid="outcome-label">
          {result.outcome_label}
        </h1>
        <p className="mt-1 font-mono-ff text-sm text-ff-muted">Final Score: {result.final_score}/100</p>

        <div className="mt-6 flex items-center justify-center gap-8">
          <div>
            <div className="flex items-center justify-center gap-1 text-xs text-ff-muted"><Coins size={13} /> Gold</div>
            <div className={`font-mono-ff text-3xl font-bold ${positive ? "text-ff-success" : "text-ff-danger"}`} data-testid="gold-result">
              {positive ? "+" : ""}<GoldCounter value={result.gold_delta} />
            </div>
          </div>
          <div className="h-10 w-px bg-ff-brass/30" />
          <div>
            <div className="flex items-center justify-center gap-1 text-xs text-ff-muted"><Medal size={13} /> Reputation</div>
            <div className={`font-mono-ff text-3xl font-bold ${result.reputation_delta >= 0 ? "text-ff-success" : "text-ff-danger"}`} data-testid="reputation-result">
              {result.reputation_delta >= 0 ? "+" : ""}{result.reputation_delta}
            </div>
          </div>
        </div>
      </div>

      {/* breakdown */}
      <div className="ff-panel mt-4 rounded-md p-6">
        <h3 className="mb-4 font-display text-xl font-semibold">What Shaped Your Fate</h3>
        <div className="space-y-3">
          {bars.map((bar) => (
            <div key={bar.label}>
              <div className="mb-1 flex justify-between text-xs">
                <span className="text-ff-muted">{bar.label}</span>
                <span className="font-mono-ff text-ff-parchment">{bar.value} / {bar.max}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-ff-bg">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.max(0, (bar.value / bar.max) * 100)}%` }}
                  transition={{ duration: 0.8 }}
                  className="h-full bg-ff-gold"
                />
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between rounded-sm bg-ff-bg/50 px-3 py-2 text-xs">
            <span className="text-ff-muted">Random Event Effect (disclosed)</span>
            <span className={`font-mono-ff font-bold ${b.random_factor >= 0 ? "text-ff-success" : "text-ff-danger"}`}>
              {b.random_factor >= 0 ? "+" : ""}{b.random_factor}
            </span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ff-success">Choices that helped</p>
            {result.helped.length ? (
              <ul className="space-y-1 text-sm text-ff-parchment">
                {result.helped.map((h, i) => <li key={i} className="flex gap-1.5"><CheckCircle size={15} className="mt-0.5 shrink-0 text-ff-success" />{h}</li>)}
              </ul>
            ) : <p className="text-sm text-ff-muted">None stood out.</p>}
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ff-danger">Choices that hurt</p>
            {result.hurt.length ? (
              <ul className="space-y-1 text-sm text-ff-parchment">
                {result.hurt.map((h, i) => <li key={i} className="flex gap-1.5"><Warning size={15} className="mt-0.5 shrink-0 text-ff-danger" />{h}</li>)}
              </ul>
            ) : <p className="text-sm text-ff-muted">No major missteps.</p>}
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3 rounded-sm bg-ff-bg/50 p-3">
          <div className="flex -space-x-2">
            {result.crew_used.map((c) => (
              <img key={c.id} src={c.portrait} alt={c.name} title={c.name} className="h-9 w-9 rounded-full border-2 border-ff-bg2 object-cover" />
            ))}
          </div>
          <span className="text-xs text-ff-muted">Crew contribution factor: <span className="font-mono-ff text-ff-gold">{result.crew_bonus}</span> · Skill score: <span className="font-mono-ff text-ff-gold">{result.skill_score}</span></span>
        </div>

        {result.crew_stress_note && (
          <p className="mt-3 rounded-sm bg-ff-danger/10 px-3 py-2 text-xs text-ff-danger/90">{result.crew_stress_note}</p>
        )}
      </div>

      {result.rank_up && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", delay: 0.2 }}
          className="ff-panel mt-4 flex items-center gap-3 rounded-md border-2 border-ff-gold p-5 ff-glow-gold"
          data-testid="rank-up-banner"
        >
          <Crown size={36} weight="duotone" className="text-ff-gold" />
          <div>
            <div className="font-mono-ff text-[10px] uppercase tracking-widest text-ff-brass">Promotion</div>
            <div className="font-display text-2xl font-bold text-ff-gold">You are now a {result.new_rank}!</div>
          </div>
        </motion.div>
      )}

      {result.crew_impact?.length > 0 && (
        <div className="ff-panel mt-4 rounded-md p-6" data-testid="crew-impact">
          <h3 className="mb-4 flex items-center gap-2 font-display text-xl font-semibold">
            <Users size={20} weight="duotone" className="text-ff-gold" /> Crew Impact
          </h3>
          <div className="space-y-3">
            {result.crew_impact.map((c) => (
              <div key={c.id} className="flex items-center gap-3 rounded-sm border border-ff-brass/20 bg-ff-bg/40 p-3" data-testid={`crew-impact-${c.id}`}>
                <img src={c.portrait} alt={c.name} className="h-11 w-11 rounded-sm object-cover" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-sm font-medium text-ff-parchment">
                    {c.name}
                    {c.leveled_up && <span className="rounded-sm bg-ff-gold/20 px-1.5 py-0.5 text-[10px] font-bold text-ff-gold">LEVEL UP → {c.new_level}</span>}
                    {c.injured && <span className="rounded-sm bg-ff-danger/20 px-1.5 py-0.5 text-[10px] font-bold text-ff-danger">INJURED ({c.severity})</span>}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-3 font-mono-ff text-[11px]">
                    <span className="text-ff-brass">XP +{c.xp_gain}</span>
                    <span className={c.stress_delta <= 0 ? "text-ff-success" : "text-ff-danger"}>Stress {c.stress_delta >= 0 ? "+" : ""}{c.stress_delta}</span>
                    <span className={c.loyalty_delta >= 0 ? "text-ff-success" : "text-ff-danger"}>Loyalty {c.loyalty_delta >= 0 ? "+" : ""}{c.loyalty_delta}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {result.medical_supplies_awarded > 0 && (
            <p className="mt-3 rounded-sm bg-ff-success/10 px-3 py-2 text-xs text-ff-success">
              +{result.medical_supplies_awarded} Medical Supplies earned — use them in the Recovery Camp.
            </p>
          )}
        </div>
      )}

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <button onClick={onReplay} data-testid="replay-btn" className="flex-1 rounded-sm bg-ff-gold py-3.5 font-semibold text-ff-bg transition-all hover:bg-ff-brass active:scale-95">
          New Expedition
        </button>
        <button onClick={onDashboard} data-testid="result-dashboard-btn" className="flex-1 rounded-sm border border-ff-brass/40 py-3.5 font-semibold text-ff-parchment transition-colors hover:border-ff-gold hover:text-ff-gold">
          Back to Dashboard
        </button>
      </div>
    </motion.div>
  );
}
